/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 30/10/2015
 * Upgraded: 20/03/2023
 */
import _ from 'underscore';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { OrderEntryWebshopService } from '../../webshop/order-entry-webshop.service';
import { ICountry, ICountryExtended, ILocationExtended, IState } from '@type_definitions/datatypes';
import { OrderEntryLocationService } from '../order-entry-location.service';
import { ILocationOption, ILocationSubCompany, IOrderEntryAddress, LocationDataType, OrderEntryAddressType, OrderEntryLocationDataService } from '../order-entry-location-data.service';
import { OrderEntryRequesterService } from '../../requester/order-entry-requester.service';
import { OrderEntryLocationFormService } from '../order-entry-location-form.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { DialogSize, GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { LocationEditComponent } from '@app/main/userprofile/user-info/location-edit.component';
import { BaseLocationAddressComponent, LOCATION_ADDRESS_TEMPLATE } from './base-location-address.component';
import { CountryDataService } from '@app/shared/location/country-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { Override } from '@app/shared/app-decorators';
import { util } from '@app/shared/util';

@Component({
  selector: 'order-location-address-existing',
  template: LOCATION_ADDRESS_TEMPLATE
})
export class OrderLocationAddressExistingComponent extends BaseLocationAddressComponent implements OnInit {
  private static ADDMODE:string = 'add';
  private editLocationButton:IExtraFieldConfigurationObject;
  private searchCountryField:IExtraFieldConfigurationObject;
  private locationField:IExtraFieldConfigurationObject;
  private searchStateField:IExtraFieldConfigurationObject;

  constructor(
    private authService:AuthService,
    private messageService:GaMessageService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private translationService:TranslationService,
    protected formlyService:GaFormlyService,
    protected orderEntryLocationService:OrderEntryLocationService,
    protected orderEntryWebshopService:OrderEntryWebshopService,
    protected countryDataService:CountryDataService,
    protected orderEntryLocationDataService:OrderEntryLocationDataService,
    protected orderEntryLocationFormService:OrderEntryLocationFormService,
    protected deferredService:GaDeferredService,
    validateService:GaValidateService,
    changeDetectorRef:ChangeDetectorRef
  ) {
    super(
      countryDataService,
      formlyService,
      orderEntryLocationService,
      orderEntryLocationDataService,
      orderEntryLocationFormService,
      orderEntryWebshopService,
      validateService,
      changeDetectorRef,
      deferredService
    );
  }

  @Override(BaseLocationAddressComponent)
  public ngOnInit():void {
    super.ngOnInit();

    // trigger change for first time
    this.onSearchCountryChange();
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const addressFields:IExtraFieldConfigurationObject[] = super.createFields();

    if (this.typeId === OrderEntryAddressType.EXISTING_LOCATION && this.addEditAllowed) {
      this.editLocationButton = this.formlyService.aLink('GA.LOCATION.EDIT.EDIT_LOCATION')
        .withWrapperClass('col col-auto ms-auto pe-1')
        .withLabelClass('d-none')
        .withValueClass('col-12')
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn'
          }
        })
        .onClick(() => this.addEditLocation())
        .isDisabled()
        .build();
      const addButton:IExtraFieldConfigurationObject = this.formlyService.aLink('GA.LOCATION.EDIT.ADD_LOCATION')
        .withLabelClass('d-none')
        .withValueClass('col-12')
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn'
          }
        })
        .onClick(() => this.addEditLocation(OrderLocationAddressExistingComponent.ADDMODE))
        .withWrapperClass('col col-auto ps-0')
        .build();

      const fields:IExtraFieldConfigurationObject[] = [
        this.editLocationButton,
        addButton
      ];
      if (this.dataType === LocationDataType.SHIPPING && this.orderEntryWebshopService.shopContext.shippingAllowDefaultAddress) {
        fields.unshift(this.orderEntryLocationFormService.createDefaultAddressField());
      }

      const defaultEditAddGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup(undefined)
        .withFields(fields)
        .withLabelClass('d-none')
        .withValueClass('col-md-11 col-sm-11')
        .build();

      addressFields.push(defaultEditAddGroup);
    }

    this.searchCountryField = this.orderEntryLocationFormService.createCountryField(this.onSearchCountryChange, 'searchCountry', 'ORDERENTRY.LOCATION.SEARCH_BY_COUNTRY');
    this.searchCountryField.templateOptions.required = !this.fixedList;
    this.searchStateField = this.orderEntryLocationFormService.createStateField(this.onSearchStateChange, 'searchState', 'ORDERENTRY.LOCATION.SEARCH_BY_STATE');
    this.searchStateField.templateOptions.required = false;
    this.locationField = this.orderEntryLocationFormService.createLocationField(this.onExistingLocationChange);

    return [
      this.searchCountryField,
      this.searchStateField,
      this.locationField
    ].concat(addressFields);
  }

  protected fillFields():void {
    super.fillFields();
    this.formlyService.fillField(this.searchCountryField, this.countryDataService.getAllCountries());
  }

  public addEditLocation(mode?:string):void {
    const init:IGADeferred<void> = this.deferredService.defer();
    // set model
    if(mode === OrderLocationAddressExistingComponent.ADDMODE) {
      this.addEditModel = {
        subCompany: this.authService.getCurrentUser().subCompany
      };
      init.resolve();
    } else {
      this.editLocationButton.templateOptions.busy = true;
      this.orderEntryLocationDataService.loadLocation(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, this.addressModel.location.id)
        .then((location:ILocationExtended) => {
          this.addEditModel = location;
        })
        .finally(() => {
          this.editLocationButton.templateOptions.busy = false;
          init.resolve();
        });
    }

    init.promise.then(() => {
      if (!this.addEditModel.country) {
        this.addEditModel.country = this.addressModel.searchCountry;
      }

      // get the list of countries
      let subCompaniesPromise:Promise<ILocationSubCompany[]>;

      // get sub companies
      if (this.orderEntryWebshopService.shopContext.customerHasSubCompanies) {
        subCompaniesPromise = this.orderEntryLocationDataService.loadSubCompanies(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id);
      }

      const onSaveLocation:Function = (location:ILocationExtended):void => {
        addEditLocationDialog.content.inputs.saving = true;
        if (mode === OrderLocationAddressExistingComponent.ADDMODE) {
          this.orderEntryLocationDataService.addLocation(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, location)
            .then(location => {
              this.addAndSelectNewLocation(location);
              onClose();
              this.addEditModel = {};
            })
            .finally(() => {
              addEditLocationDialog.content.inputs.saving = false;
            });
        } else {
          this.orderEntryLocationDataService.updateLocation(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, this.addressModel.location.id, this.transformLocationModel(location))
            .then((location:ILocationExtended) => {
              this.updateAndSelectNewLocation(location);
              onClose();
              this.addEditModel = {};
            })
            .finally(() => {
              addEditLocationDialog.content.inputs.saving = false;
            });
        }
      };

      const onClose:Function = ():void => {
        addEditLocationDialog.hide();
      };

      const addEditLocationDialog:IDialog = this.messageService.showDialog({
        component: LocationEditComponent,
        wrapperComponent: false,
        inputs: {
          entity: 'GA.GENERAL.LOCATION',
          adding: mode === OrderLocationAddressExistingComponent.ADDMODE,
          model: this.addEditModel,
          customerSubCompanyMandatory: this.orderEntryWebshopService.shopContext.customerSubCompanyMandatory,
          customerCurrencies: this.orderEntryWebshopService.shopContext.customerCurrencies,
          subCompaniesPromise,
          allCountriesPromise: this.orderEntryLocationDataService.allCountries()
        },
        outputs: {
          onSave: onSaveLocation,
          onCancel: onClose
        },
        size: DialogSize.MD
      });
    });
  }

  private addAndSelectNewLocation(location:ILocationExtended):void {
    // if editable property is not exist after adding new then it should still keep editable state
    if (util.isNullOrUndefinedOrEmpty(location.editable)) {
      location.editable = true;
    }

    if ((<ICountry>location.country).id === this.addressModel.searchCountry.id) {
      // add to list and select
      // get field and add to options
      const locationField:IExtraFieldConfigurationObject = _.findWhere(this.addressFields, { key: 'location' });
      const options:any[] = _.clone(locationField.templateOptions.options);
      options.unshift(location);
      locationField.templateOptions.options = options;
      // set model to location
      this.addressModel.location = locationField.templateOptions.options[ 0 ];
      this.addToCache(location);
    } else {
      this.newLocationWarning((<ICountryExtended>location.country));
    }
  }

  private onSearchCountryChange = (country?:ICountryExtended, oldCountry?:ICountryExtended):void => {
    country = country || this.addressModel.searchCountry;

    const resetLocationField:Function = ():void => {
      this.locationField.templateOptions.initialValue = undefined;
      this.addressForm.controls?.location?.reset(undefined, { emitEvent: true });
    };

    this.showHideStateField(country, oldCountry, this.searchStateField, 'searchState');

    // clear the location field when search country is changed as is not in the list of locations
    if (oldCountry && country?.id !== oldCountry?.id) {
      resetLocationField();
    }
    this.loadLocations(country?.id);
  };

  private onSearchStateChange = (state:IState):void => {
    if(state?.id) {
      this.loadLocations((<ICountryExtended>this.addressModel.location?.country)?.id, state.id);
    }
  };

  private loadLocations(countryId?:number, stateId?:string):void {
    const requesterId:number = this.orderEntryRequesterService.contact?.id;
    const selectedShopId:number = this.orderEntryWebshopService.selectedShop.id;
    const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry.id;
    const billingType:string = this.orderEntryWebshopService.shopContext.billingType;
    const bpId:number = this.orderEntryWebshopService.selectedBP.id;

    this.formlyService.fillField(
      this.locationField, 
      this.orderEntryLocationDataService.locations(this.dataType, countryId, stateId, requesterId, selectedShopId, selectedCountryId, billingType, bpId)
        .then((locationOptions:ILocationOption[]) => {
          this.locationToModel.locationOptions = _.clone(locationOptions);
  
          const locations:ILocationExtended[] = _.pluck(locationOptions, 'location');
  
          // when only 1 address -> skip, already preselected it in fillField()
          if (locations?.length !== 1) {
            let selectedLocation:ILocationExtended;
  
            if (this.loadedAddress?.id) {
              selectedLocation = _.find(locations, (loc:ILocationExtended) => loc.id === this.loadedAddress.location.id);
            }
  
            // if there is a default location, select that
            if(!selectedLocation && locationOptions?.length){
              // if there is a default location, select that
              const selectAddress:IOrderEntryAddress = _.find(locationOptions, (address:IOrderEntryAddress) => address.default);
              selectedLocation = selectAddress?.location;
            }
  
            if (selectedLocation) {
              this.addressForm.patchValue(this.getInitialModel(selectedLocation));
            }
          }
  
          return locations;
        })
        .finally(() => {
          this.onValidationReadyDeferred.resolve();
        })
      , true);
  }

  private addToCache(newLocation:ILocationExtended):void {
    const countryId:number = this.addressModel.searchCountry.id;
    const stateId:string = this.addressModel.searchState? this.addressModel.searchState.id : undefined;
    const requesterId:number = this.orderEntryRequesterService.contact?.id;
    const selectedShopId:number = this.orderEntryWebshopService.selectedShop.id;
    const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry.id;
    const billingType:string = this.orderEntryWebshopService.shopContext.billingType;
    const bpId:number = this.orderEntryWebshopService.selectedBP.id;

    this.orderEntryLocationDataService.locations(this.dataType, countryId, stateId, requesterId, selectedShopId, selectedCountryId, billingType, bpId)
      .then((location:Partial<IOrderEntryAddress>[]) => {
        const idx:number = _.findIndex(location, (loc:IOrderEntryAddress) => loc.location.id === newLocation.id);
        if (idx === -1) { //add
          location.unshift({ location: newLocation });
        } else {
          location[ idx ].location = newLocation;
        }
      });
  }

  private newLocationWarning(locationCountry:ICountryExtended):void {
    const title = this.translationService.instant('GA.LOCATION.EDIT.ADD_COUNTRY_WARNING_TITLE');
    const message = this.translationService.instant('GA.LOCATION.EDIT.ADD_COUNTRY_WARNING_MESSAGE', { countryname: locationCountry.name });
    this.messageService.showMessageDialog(title, message);
  }

  private onExistingLocationChange = (location:ILocationExtended, oldLocation?:ILocationExtended):void => {
    this.onLocationChange(location, oldLocation);
    if(this.editLocationButton){
      this.editLocationButton.templateOptions.disabled = !location || !location.editable;
    }
  };

  private updateAndSelectNewLocation(location:ILocationExtended):void {
    // add to list and select
    // get field and add to options
    const locationField:IExtraFieldConfigurationObject = _.findWhere(this.addressFields, { key: 'location' });
    const options:ILocationExtended[] = _.clone(locationField.templateOptions.options);
    const myOptionIndex:number = _.findIndex(options, { id: location.id });

    // if editable property is not exist after updating then it should still keep editable
    if (util.isNullOrUndefinedOrEmpty(location.editable)) {
      location.editable = true;
    }

    options[ myOptionIndex ] = location;
    locationField.templateOptions.options = options;
    // set model to location
    this.locationField.formControl.patchValue(locationField.templateOptions.options[ myOptionIndex ]);
    this.addToCache(location);
  }

  private transformLocationModel(locationModel:ILocationExtended):ILocationExtended {
    return _.omit(locationModel, [
      'company',
      'companyCountry'
    ]);
  }
}